/**
 * Utility functions for Stripe test mode detection
 * Detects test mode based on 'stripetest' key in username or email
 */

import { supabase } from "../config/supabaseClient";
import { StaticVariables } from "./StaticVariables";

/**
 * Stripe Checkout / Customer Portal custom domain.
 * Must be `payments.browsable.ai` (`.com` has no DNS — NXDOMAIN).
 */
export const STRIPE_PAYMENTS_ORIGIN = "https://payments.browsable.ai";

/** Customer portal login link path segment (unchanged across domain migration). */
export const STRIPE_CUSTOMER_PORTAL_LOGIN_ID = "14kg1ecrU8Lt8vK4gg";

export function getStripeCustomerPortalUrl(): string {
  return `${STRIPE_PAYMENTS_ORIGIN}/p/login/${STRIPE_CUSTOMER_PORTAL_LOGIN_ID}`;
}

/**
 * Synchronous test mode detection for immediate use
 * Checks localStorage and URL parameters only
 */

export const checkTestEmail = (email: string) => {
  
    if (email && email.toLowerCase().includes('stripetest')) {
      return true;
    }
    return false;
}
export function isStripeTestModeSync(): boolean {
    // Check if we are in subscription-test route
    if (window.location.pathname.includes('subscribe-test')) {
        return true;
    } 
  // Check username from localStorage
  const username = localStorage.getItem("loggedIn");
  if (username && username.toLowerCase().includes('stripetest')) {
    return true;
  }

  // Check if we're in admin/member mode and check their data
  const adminData = localStorage.getItem("admin");
  const memberData = localStorage.getItem("member");

  if (adminData) {
    try {
      const admin = JSON.parse(adminData);
      if (
        admin.username &&
        admin.username.toLowerCase().includes("stripetest")
      ) {
        return true;
      }
      if (admin.email && admin.email.toLowerCase().includes("stripetest")) {
        return true;
      }
    } catch (e) {
      console.warn("Error parsing admin data:", e);
    }
  }

  if (memberData) {
    try {
      const member = JSON.parse(memberData);
      if (
        member.username &&
        member.username.toLowerCase().includes("stripetest")
      ) {
        return true;
      }
      if (member.email && member.email.toLowerCase().includes("stripetest")) {
        return true;
      }
    } catch (e) {
      console.warn("Error parsing member data:", e);
    }
  }

  return false;
}

/**
 * Asynchronous test mode detection that checks database
 * Use this when you need to check user email from database
 */
export async function isStripeTestMode(): Promise<boolean> {
  // First check synchronous detection
  if (isStripeTestModeSync()) {
    return true;
  }

  // Check email from database if username exists
  const username = localStorage.getItem("loggedIn");
  if (username) {
    try {
      const { data, error } = await supabase
        .from("user")
        .select("email")
        .eq("username", username)
        .single();

      if (
        data &&
        data.email &&
        data.email.toLowerCase().includes("stripetest")
      ) {
        return true;
      }
    } catch (e) {
      console.warn("Error checking user email from database:", e);
    }
  }

  return false;
}

/**
 * Test mode detection with email parameter
 * Use this for initial subscriptions where email is provided
 */
export function isStripeTestModeWithEmail(email?: string): boolean {
  // Check synchronous detection first
  if (isStripeTestModeSync()) {
    return true;
  }

  // Check provided email
  if (email && email.toLowerCase().includes("stripetest")) {
    return true;
  }

  return false;
}

/**
 * Gets the appropriate Stripe publishable key based on test mode
 */
export function getStripePublishableKey(): string {
  const isTestMode = isStripeTestModeSync();

  if (isTestMode) {
    // Test key - replace with your actual test key
    return "pk_test_51MavLUHoyGmWWOpVfkIMtmMb5oGGXCMNyg5j9CJgx1FAxnv54UUhLsazs4lyVK7c79u07lVbKDdtxoOXslGlm2r900TiOnwzCj";
  } else {
    // Live key
    return "pk_live_51MavLUHoyGmWWOpVoRa49CVTMgqsBLKtB7Jw6JHRB2OTNZRujLCFAdFJs3YqAVgXZr2dQJ9yYQfR6A01dgsbTjiB00K13Aykxr";
  }
}

/**
 * Gets the appropriate API URL based on test mode
 */
export function getApiUrl(): string {
  const base = String(StaticVariables().API_BASE).replace(/\/+$/, "");
  return `${base}/`;
}

/**
 * Adds test mode indicator to API requests
 */
export function getApiRequestHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (isStripeTestModeSync()) {
    headers["X-Test-Mode"] = "true";
  }

  return headers;
}

/**
 * Gets the appropriate Stripe price IDs based on test mode
 */
export function getStripePriceIds(): Record<string, string> {
  const isTestMode = isStripeTestModeSync();

  if (isTestMode) {
    // Test price IDs - replace with your actual test price IDs
    return {
      starter: "price_1R24owHoyGmWWOpVi4y9sEug", // Replace with actual test price ID
      pro: "price_1R24qAHoyGmWWOpVmySpQZr3", // Replace with actual test price ID
      growth: "price_1R24qbHoyGmWWOpVeTv3W0fd", // Replace with actual test price ID
    };
  } else {
    // Live price IDs
    return {
      starter: "price_1PRqPnHoyGmWWOpVQGZXWcxd", // $19/month Starter Plan
      pro: "price_1Q2bzwHoyGmWWOpVfTr1t8PS", // $49/month Pro Plan
      growth: "price_1PRqDuHoyGmWWOpVv0wXk20W", // $99/month Growth Plan
    };
  }
}

/**
 * Gets a specific price ID based on plan type and test mode
 */
export function getPriceId(planType: "starter" | "pro" | "growth"): string {
  const priceIds = getStripePriceIds();
  return priceIds[planType];
}

/** Blog add-on plans (generation + publishing credits). */
export type BlogStripePlan = "blog_4" | "blog_12";

/**
 * Live Blog add-on products (Stripe Dashboard, Oct 2026).
 * Checkout resolves the active monthly price from the product id server-side.
 * Detection also matches product/plan names containing "blog 4" / "blog 12".
 */
export const BLOG_LIVE_PRODUCT_IDS: Record<BlogStripePlan, string> = {
  blog_4: "prod_VWUVUK5OSiy2M3",
  blog_12: "prod_VWUXrMdXBFaikH",
};

/**
 * Optional explicit price IDs. Empty → cron_job resolves default recurring price
 * from the product (preferred while prices stay $0 / change often).
 */
export function getBlogStripePriceIds(): Record<BlogStripePlan, string> {
  const isTestMode = isStripeTestModeSync();
  if (isTestMode) {
    return {
      blog_4: "",
      blog_12: "",
    };
  }
  return {
    blog_4: "",
    blog_12: "",
  };
}

export function getBlogPriceId(planType: BlogStripePlan): string {
  return getBlogStripePriceIds()[planType];
}

export function getBlogProductIds(): Record<BlogStripePlan, string> {
  // Live products are the source of truth. Test checkout still uses the same
  // product ids unless dedicated test products are created later.
  return { ...BLOG_LIVE_PRODUCT_IDS };
}

export function getBlogProductId(planType: BlogStripePlan): string {
  return getBlogProductIds()[planType];
}

/**
 * Live payment links for Blog add-ons (optional).
 * Path segment after `/b/` is sent as `paymentLinkId` (same as LiB plans).
 */
export function getBlogStripePaymentLinks(): Record<BlogStripePlan, string> {
  return {
    blog_4: `${STRIPE_PAYMENTS_ORIGIN}/b/blog_4`,
    blog_12: `${STRIPE_PAYMENTS_ORIGIN}/b/blog_12`,
  };
}

export function getBlogPaymentLinkUrl(planType: BlogStripePlan): string {
  return getBlogStripePaymentLinks()[planType];
}

export function getBlogPaymentLinkId(planType: BlogStripePlan): string {
  const url = getBlogPaymentLinkUrl(planType);
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || url;
  } catch {
    const parts = url.split("/").filter(Boolean);
    return parts[parts.length - 1] || url;
  }
}

/**
 * Gets the appropriate Stripe product IDs based on test mode
 */
export function getStripeProductIds(): Record<string, string> {
  const isTestMode = isStripeTestModeSync();

  if (isTestMode) {
    // Test product IDs - replace with your actual test product IDs
    return {
      starter: "prod_test_starter", // Replace with actual test product ID
      pro: "prod_test_pro", // Replace with actual test product ID
      growth: "prod_test_growth", // Replace with actual test product ID
    };
  } else {
    // Live product IDs
    return {
      starter: "prod_ObqmCF2H0gVujs",
      pro: "prod_QuQraibVi8Lih8",
      growth: "prod_ObqmzJg4bqbE2X",
    };
  }
}

/**
 * Gets a specific product ID based on plan type and test mode
 */
export function getProductId(planType: "starter" | "pro" | "growth"): string {
  const productIds = getStripeProductIds();
  return productIds[planType];
}

/**
 * Live payment links for each plan.
 * We pass `paymentLinkId` to the backend (derived from the URL path).
 * NOTE: Test mode intentionally does not use payment links to avoid impacting test flows.
 */
export function getStripePaymentLinks(): Record<"starter" | "pro" | "growth", string> {
  return {
    starter: "https://payments.browsable.ai/b/4gw9CF5xGaYs7gQ4gm",
    pro: "https://payments.browsable.ai/b/aEU3eh4tCc2wbx66ow",
    growth: "https://payments.browsable.ai/b/6oE0256BKfeI7gQ9AF",
  };
}

export function getPaymentLinkUrl(planType: "starter" | "pro" | "growth"): string {
  return getStripePaymentLinks()[planType];
}

/**
 * The backend wants a `paymentLinkId`. For our custom domain links, the stable identifier
 * we have client-side is the last URL path segment after `/b/`.
 */
export function getPaymentLinkId(planType: "starter" | "pro" | "growth"): string {
  const url = getPaymentLinkUrl(planType);
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || url;
  } catch {
    // Fallback: best-effort extraction
    const parts = url.split("/").filter(Boolean);
    return parts[parts.length - 1] || url;
  }
}

/**
 * Detects test mode from Stripe checkout session ID
 * Test sessions start with 'cs_test_' while live sessions start with 'cs_live_'
 */
export function isTestModeFromSessionId(sessionId: string): boolean {
  return sessionId.startsWith("cs_test_");
}

/**
 * Gets test mode status for checkout session verification
 * Combines session ID detection with other test mode detection methods
 */
export function getTestModeForVerification(sessionId: string): boolean {
  return isTestModeFromSessionId(sessionId) || isStripeTestModeSync();
}

/**
 * Special function for initial subscription checkout
 * This function should be called when creating checkout sessions for new users
 * It checks both URL parameters and provided email for test mode detection
 */
export function getCheckoutSessionData(email?: string) {
  const isTestMode = isStripeTestModeWithEmail(email);
  const priceIds = getStripePriceIds();

  return {
    isTestMode,
    priceIds,
    headers: getApiRequestHeaders(),
    apiUrl: getApiUrl(),
    stripeKey: getStripePublishableKey(),
  };
}

/**
 * Logs test mode status for debugging
 */
export function logTestModeStatus(): void {
  const isTestMode = isStripeTestModeSync();

  if (isTestMode) {
    // Log current price IDs being used
    const priceIds = getStripePriceIds();
  } else {
    // Log current price IDs being used
    const priceIds = getStripePriceIds();
  }
}
